import React, { useState, useEffect } from 'react';
import { X, Send, AlertTriangle } from 'lucide-react';
import axios from '../api/axios';
import styles from './MessageComposer.module.css';

interface Leader {
  id: number;
  username: string;
  avatarUrl?: string;
  position?: string;
  department?: string;
}

interface MessageComposerProps {
  onClose: () => void;
  onSent: () => void;
  replyToMessageId?: number;
  recipientId?: number;
  recipientName?: string;
  replySubject?: string;
}

const MessageComposer: React.FC<MessageComposerProps> = ({
  onClose,
  onSent,
  replyToMessageId,
  recipientId,
  recipientName,
  replySubject
}) => {
  const [leaders, setLeaders] = useState<Leader[]>([]);
  const [selectedRecipient, setSelectedRecipient] = useState<number | null>(recipientId || null);
  const [messageType, setMessageType] = useState<number>(1); // Briefing
  const [priority, setPriority] = useState<number>(1); // Normal
  const [subject, setSubject] = useState(replySubject || '');
  const [content, setContent] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!replyToMessageId) {
      fetchLeaders();
    }
  }, [replyToMessageId]);

  const fetchLeaders = async () => {
    try {
      const response = await axios.get('/LeadershipMessages/leaders');
      setLeaders(response.data);
    } catch (err) {
      console.error('Error fetching leaders:', err);
      setError('Failed to load recipient list');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!selectedRecipient) {
      setError('Please select a recipient');
      return;
    }

    if (!subject.trim()) {
      setError('Please enter a subject');
      return;
    }

    if (!content.trim()) {
      setError('Please enter a message');
      return;
    }

    try {
      setSending(true);
      setError(null);

      console.log('Sending message:', {
        recipientId: selectedRecipient,
        type: messageType,
        priority,
        subject: subject.trim(),
        content: content.trim(),
        parentMessageId: replyToMessageId
      });

      const response = await axios.post('/LeadershipMessages', {
        recipientId: selectedRecipient,
        type: messageType,
        priority,
        subject: subject.trim(),
        content: content.trim(),
        parentMessageId: replyToMessageId
      });

      console.log('Message sent successfully:', response.data);

      onSent();
      onClose();
    } catch (err: any) {
      console.error('Error sending message:', err);
      console.error('Error response:', err.response);
      console.error('Error status:', err.response?.status);
      console.error('Error data:', err.response?.data);
      setError(err.response?.data?.message || err.response?.data || 'Failed to send message');
    } finally {
      setSending(false);
    }
  };

  const messageTypes = [
    { value: 1, label: 'Briefing', icon: '📋', description: 'General updates and briefings' },
    { value: 2, label: 'Request', icon: '❓', description: 'Request information or action' },
    { value: 3, label: 'Directive', icon: '📢', description: 'Official directive or order' },
    { value: 4, label: 'Update', icon: '📰', description: 'Status or progress update' },
    { value: 5, label: 'Collaboration', icon: '🤝', description: 'Collaborative discussion' }
  ];

  const priorities = [
    { value: 1, label: 'Normal', color: '#3b82f6' },
    { value: 2, label: 'High', color: '#f59e0b' },
    { value: 3, label: 'Urgent', color: '#ef4444' }
  ];

  return (
    <div className={styles.overlay} onClick={onClose}>
      <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
        <div className={styles.header}>
          <div className={styles.headerContent}>
            <Send size={24} />
            <h2>{replyToMessageId ? 'Reply to Message' : 'Compose Message'}</h2>
          </div>
          <button className={styles.closeBtn} onClick={onClose}>
            <X size={24} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className={styles.form}>
          {error && (
            <div className={styles.error}>
              <AlertTriangle size={18} />
              {error}
            </div>
          )}

          {!replyToMessageId && (
            <div className={styles.field}>
              <label>To *</label>
              {recipientName ? (
                <div className={styles.recipientName}>{recipientName}</div>
              ) : (
                <select
                  value={selectedRecipient || ''}
                  onChange={(e) => setSelectedRecipient(Number(e.target.value))}
                  required
                  className={styles.select}
                >
                  <option value="">Select recipient...</option>
                  {leaders.map((leader) => (
                    <option key={leader.id} value={leader.id}>
                      {leader.username}
                      {leader.position && ` - ${leader.position}`}
                      {leader.department && ` (${leader.department})`}
                    </option>
                  ))}
                </select>
              )}
            </div>
          )}

          {!replyToMessageId && (
            <div className={styles.field}>
              <label>Message Type *</label>
              <div className={styles.typeGrid}>
                {messageTypes.map((type) => (
                  <button
                    key={type.value}
                    type="button"
                    className={`${styles.typeBtn} ${messageType === type.value ? styles.active : ''}`}
                    onClick={() => setMessageType(type.value)}
                    title={type.description}
                  >
                    <span className={styles.typeIcon}>{type.icon}</span>
                    <span className={styles.typeLabel}>{type.label}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {!replyToMessageId && (
            <div className={styles.field}>
              <label>Priority *</label>
              <div className={styles.priorityButtons}>
                {priorities.map((p) => (
                  <button
                    key={p.value}
                    type="button"
                    className={`${styles.priorityBtn} ${priority === p.value ? styles.active : ''}`}
                    onClick={() => setPriority(p.value)}
                    style={{
                      borderColor: priority === p.value ? p.color : 'var(--border-color)',
                      backgroundColor: priority === p.value ? p.color : 'transparent',
                      color: priority === p.value ? 'white' : 'var(--text-primary)'
                    }}
                  >
                    {p.label}
                  </button>
                ))}
              </div>
            </div>
          )}

          {!replyToMessageId && (
            <div className={styles.field}>
              <label>Subject *</label>
              <input
                type="text"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                placeholder="Enter message subject"
                maxLength={200}
                required
                className={styles.input}
              />
              <div className={styles.charCount}>{subject.length}/200</div>
            </div>
          )}

          {replyToMessageId && (
            <div className={styles.field}>
              <label>Subject</label>
              <div className={styles.recipientName}>{subject}</div>
            </div>
          )}

          <div className={styles.field}>
            <label>{replyToMessageId ? 'Reply *' : 'Message *'}</label>
            <textarea
              value={content}
              onChange={(e) => setContent(e.target.value)}
              placeholder="Enter your message..."
              required
              className={styles.textarea}
              rows={10}
            />
          </div>

          <div className={styles.footer}>
            <button type="button" className={styles.btnSecondary} onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className={styles.btnPrimary} disabled={sending}>
              {sending ? 'Sending...' : (
                <>
                  <Send size={18} />
                  Send Message
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default MessageComposer;
